package com.advaita.cloud.drive.ui.store

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.advaita.cloud.drive.shared.GivenShareDto
import com.advaita.cloud.drive.shared.GivenSharesResponse
import com.advaita.cloud.drive.shared.PrincipalRef
import com.advaita.cloud.drive.shared.SourceStatus
import com.advaita.cloud.drive.ui.api.FilesApi
import com.advaita.cloud.drive.ui.api.RedirectingException
import com.advaita.cloud.drive.ui.api.ShareApiException
import com.advaita.cloud.drive.ui.components.ShareCap
import com.advaita.cloud.drive.ui.components.capForError
import com.advaita.cloud.drive.ui.components.capForSharedList
import com.advaita.cloud.drive.ui.components.capSentence
import kotlinx.coroutines.CancellationException

/** The read and the one write that Shared by me needs. Injected, so jsTest can script them. */
interface GivenBackend {
    suspend fun given(): GivenSharesResponse
    suspend fun revoke(grantId: String)
}

object HttpGivenBackend : GivenBackend {
    override suspend fun given() = FilesApi.listGivenShares()
    override suspend fun revoke(grantId: String) = FilesApi.revokeShare(grantId)
}

/** The words of the confirm dialog and the toast for Stop sharing. */
data class StopPrompt(val title: String, val message: String, val done: String)

fun stopPrompt(item: String, recipient: String) = StopPrompt(
    title = "Stop sharing",
    message = "Stop sharing $item with $recipient? They lose access, and so do any shares they made from it.",
    done = "Stopped sharing $item with $recipient.",
)

/** Shown when a stop fails for a reason that is not a cap. */
const val STOP_FAILED_SENTENCE = "Could not stop this share. Try again."

/** Shown when the list cannot load for a reason that is not a cap. */
const val GIVEN_LOAD_FAILED_SENTENCE = "Could not load your shares. Try again."

/**
 * CR-6.3 (D10): the state of Shared by me. DOM-free, like [SharedWithMeStore].
 *
 * The list is `GET /api/shares/given`. An outage is a banner over the rows of the last good read,
 * and with none, no table, so "You have not shared anything yet" never stands in for an outage.
 * A 404 means the checks are off ([ShareCap.CHECKS_OFF]).
 */
class SharedByMeStore(
    private val backend: GivenBackend = HttpGivenBackend,
    private val now: () -> Double = { kotlin.js.Date.now() },
) {
    private var all: List<GivenShareDto> by mutableStateOf(emptyList())
    private var peopleStatus: String by mutableStateOf(SourceStatus.OK)
    private var teamsStatus: String by mutableStateOf(SourceStatus.OK)

    /** True until the first [load] settles. */
    var loading: Boolean by mutableStateOf(true)
        private set
    var cap: ShareCap? by mutableStateOf(null)
        private set
    var error: String? by mutableStateOf(null)
        private set

    /** The search box text. */
    var query: String by mutableStateOf("")

    private var loadedOnce = false

    val visibleRows: List<GivenShareDto>
        get() {
            val q = query.trim().lowercase()
            return if (q.isEmpty()) all else all.filter { (it.itemName ?: "").lowercase().contains(q) }
        }

    /**
     * False when a cap, an error, or an outage with no rows stands in the list's place. An outage
     * after a good read keeps the rows of that read under [bannerSentence].
     */
    val showTable: Boolean
        get() = error == null && !(loading && !loadedOnce) && (cap == null || (cap == ShareCap.OUTAGE && loadedOnce))

    /** The banner over the rows of the last good read, when a later read hit an outage. */
    val bannerSentence: String? get() = if (showTable && cap != null) capSentence(cap!!) else null

    /** True when the read worked and the caller has shared nothing at all. Never during an outage. */
    val isEmpty: Boolean get() = showTable && cap == null && all.isEmpty()

    /** True when rows exist and the search hides every one. */
    val noMatch: Boolean get() = showTable && all.isNotEmpty() && visibleRows.isEmpty()

    val count: Int get() = all.size

    suspend fun load() {
        loading = true
        try {
            show(backend.given())
        } catch (e: CancellationException) {
            throw e
        } catch (e: RedirectingException) {
            throw e
        } catch (e: ShareApiException) {
            val c = capForSharedList(e)
            if (c != null) { cap = c; error = null } else { cap = null; error = GIVEN_LOAD_FAILED_SENTENCE }
        } catch (t: Throwable) {
            cap = null
            error = GIVEN_LOAD_FAILED_SENTENCE
        } finally {
            loading = false
        }
    }

    private fun show(page: GivenSharesResponse) {
        all = page.shares
        peopleStatus = page.peopleStatus
        teamsStatus = page.teamsStatus
        cap = null
        error = null
        loadedOnce = true
    }

    fun itemLabel(row: GivenShareDto): String = row.itemName?.takeIf { it.isNotBlank() } ?: NAME_UNAVAILABLE

    fun recipientLabel(row: GivenShareDto): String {
        val ref: PrincipalRef = row.recipient.ref
        val status = if (row.recipient.kind == "TEAM") teamsStatus else peopleStatus
        return principalLabel(row.recipient.kind, ref, status)
    }

    fun levelText(row: GivenShareDto): String = levelLabel(row.level)

    /** The Expires cell of [row], on the store's clock. */
    fun expiry(row: GivenShareDto): ExpiryLabel = expiryLabel(row.expiresAt, now())

    /**
     * False when the end of [row] has passed. The vault already ended that grant, so the row offers
     * no Stop sharing. Example: a row kept from a good read through an outage, whose end came meanwhile.
     */
    fun stopOffered(row: GivenShareDto): Boolean = !isExpired(row.expiresAt, now())

    /**
     * Stops one share with `DELETE /api/shares/{grantId}`. Null on success, else the one sentence to
     * show. Only a 2xx is success. The vault revoke is idempotent (a repeat answers 204), so a 404
     * does not mean "already gone": the share UI gate can be shut, the grant can be in another
     * org, or the id can be unknown. On a 404 the list is read again. The stop counts as done only
     * when that read works and no longer holds the grant. Else the row stays and the sentence is
     * [STOP_FAILED_SENTENCE]. Example: Priya stops her share with Lena while the gate shuts. The
     * DELETE and the re-read are both 404, so the row stays and no "Stopped sharing" toast shows.
     */
    suspend fun stop(row: GivenShareDto): String? {
        try {
            backend.revoke(row.grantId)
        } catch (e: CancellationException) {
            throw e
        } catch (e: RedirectingException) {
            throw e
        } catch (e: ShareApiException) {
            if (e.status != 404) return capForError(e)?.let { capSentence(it) } ?: STOP_FAILED_SENTENCE
            return if (goneOnReRead(row.grantId)) null else STOP_FAILED_SENTENCE
        } catch (t: Throwable) {
            return STOP_FAILED_SENTENCE
        }
        // Gone at once, so an outage on the re-read cannot keep it on screen.
        all = all.filter { it.grantId != row.grantId }
        load()
        return null
    }

    /** True when a fresh read works and does not list [grantId]. A failed read changes nothing on screen. */
    private suspend fun goneOnReRead(grantId: String): Boolean {
        val page = try {
            backend.given()
        } catch (e: CancellationException) {
            throw e
        } catch (e: RedirectingException) {
            throw e
        } catch (t: Throwable) {
            return false
        }
        show(page)
        return page.shares.none { it.grantId == grantId }
    }
}
